/**
 * AdPreview — one way a responsive search ad can appear on a Google results
 * page: the "Sponsored" label, the display URL (final URL host + display
 * paths), three headlines joined with " | " and two descriptions. Google picks
 * the combination for each search, so "Show another combination" samples a
 * different valid one (pinned headlines stay in their positions).
 *
 * HowAdsWork — the short explanation above the creatives list, with an info
 * popup holding the details.
 */

import { useEffect, useId, useRef, useState } from 'react';
import type { AdoptRsaContentDto } from '@/lib/api';
import { RSA_LIMITS } from '../rsa-limits';
import {
  nextRsaCombination,
  rsaDisplayUrl,
  sampleRsaCombination,
  type RsaCombination,
} from '../rsa-preview';

export function AdPreview({ content }: { content: AdoptRsaContentDto }) {
  const [combination, setCombination] = useState<RsaCombination>(() =>
    sampleRsaCombination(content),
  );
  const [onlyOne, setOnlyOne] = useState(false);

  const another = (): void => {
    const next = nextRsaCombination(content, combination);
    if (next) setCombination(next);
    else setOnlyOne(true);
  };

  return (
    <div className="flex flex-col gap-2">
      <figure
        aria-label="Google search ad preview"
        className="rounded-lg border border-border bg-background p-3 max-w-xl"
      >
        <div className="text-xs font-semibold text-foreground">Sponsored</div>
        <div className="text-xs text-muted-foreground break-all">
          {rsaDisplayUrl(content.finalUrl, content.paths ?? [])}
        </div>
        <div
          className="mt-0.5 text-lg leading-snug text-blue-800 dark:text-blue-300"
          data-testid="preview-headlines"
        >
          {combination.headlines.join(' | ')}
        </div>
        <p className="text-sm text-foreground/80" data-testid="preview-descriptions">
          {combination.descriptions.join(' ')}
        </p>
        <figcaption className="sr-only">
          One combination Google could show. It chooses which headlines and descriptions to combine
          for each search.
        </figcaption>
      </figure>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={another}
          disabled={onlyOne}
          className="self-start px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
        >
          Show another combination
        </button>
        {onlyOne && (
          <span className="text-xs text-muted-foreground" role="status">
            This ad has only one combination.
          </span>
        )}
      </div>
    </div>
  );
}

export function HowAdsWork() {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="text-muted-foreground">
          Each ad is a set of headlines and descriptions. Google mixes them into the ad people see —
          use <span className="font-medium text-foreground">Preview</span> to see one.
        </p>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={panelId}
          className="shrink-0 rounded-full border border-border px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <span aria-hidden="true">ⓘ</span> How ads work
        </button>
      </div>
      {open && (
        <div
          id={panelId}
          role="note"
          className="mt-2 rounded-md border border-border bg-background p-3 text-sm flex flex-col gap-1.5"
        >
          <p>
            Google shows 2–3 of these headlines and 1–2 descriptions at a time, and tries different
            combinations to learn which work best.
          </p>
          <p>
            Limits: {RSA_LIMITS.headlines.maxChars} characters per headline,{' '}
            {RSA_LIMITS.descriptions.maxChars} per description. An ad needs{' '}
            {RSA_LIMITS.headlines.min}–{RSA_LIMITS.headlines.max} headlines and{' '}
            {RSA_LIMITS.descriptions.min}–{RSA_LIMITS.descriptions.max} descriptions, so every
            headline must read well next to any other.
          </p>
          <p>
            Each ad links to its landing page variant; the display URL shows the site and up to{' '}
            {RSA_LIMITS.paths.max} short display paths.
          </p>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              buttonRef.current?.focus();
            }}
            className="self-start text-xs text-primary hover:underline"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
